import styled, { css, keyframes } from 'styled-components';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import {
  DESKTOP_HEAD_HEIGHT,
  FOOTER_HEIGHT,
  HEAD_HEIGHT,
} from '@/components/Ad/SliderWithGallery/SliderWithGallery.constants';
import { Button } from '@/components/UI/Button/Button';
import { MODAL_ANIMATION_DURATION } from '@/styles/constants';
import { ellipsisMultiline } from '@/styles/helpers';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

export const GalleryBackground = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: #000;
  z-index: 10009;

  animation: ${({ $isVisible }) => ($isVisible ? fadeIn : fadeOut)}
    ${MODAL_ANIMATION_DURATION}ms ease-in-out forwards;
`;

export const Head = styled.div<{ $isVisible: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10100;
  width: 100%;
  height: ${DESKTOP_HEAD_HEIGHT}px;
  padding: 15px 30px 55px 30px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0;
    height: ${HEAD_HEIGHT}px;
  }

  animation: ${({ $isVisible }) => ($isVisible ? fadeIn : fadeOut)}
    ${MODAL_ANIMATION_DURATION}ms ease-in-out forwards;
`;

export const CountGallery = styled(Typography.Title)<{ $isBig: boolean }>`
  color: ${({ theme }) => theme.palette.system.white};
  padding: 5px 10px;
  border-radius: 50px;
  background: rgba(0, 0, 0, 0.25);
  position: fixed;
  bottom: 130px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10101;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: static;
    font-size: 16px;
    font-weight: 500;
    padding: 0;
    border-radius: 0;
    left: unset;
    transform: unset;
  }

  ${({ $isBig }) =>
    $isBig &&
    css`
      flex-grow: 1;
      text-align: center;
      transform: translateX(-18px);
    `}
`;

export const Price = styled(Typography.Header).attrs(() => ({
  $variant: '3',
}))`
  color: ${({ theme }) => theme.palette.system.white};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 18px;
    font-weight: 700;
  }
`;

export const Footer = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  z-index: 10100;
  width: 100%;
  bottom: 0;
  left: 0;
  gap: 20px;
  padding: 20px 20px 40px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: grid;
    grid-template-columns: 1fr 50px;
    padding: 10px 15px;
    height: ${({ theme }) =>
      theme.isWebView
        ? `${+theme.insetsBottom + FOOTER_HEIGHT}px`
        : `${FOOTER_HEIGHT}px`};
  }

  animation: ${({ $isVisible }) => ($isVisible ? fadeIn : fadeOut)}
    ${MODAL_ANIMATION_DURATION}ms ease-in-out forwards;
`;

export const Title = styled(Typography.Body).attrs(() => ({
  $variant: '1',
}))`
  ${ellipsisMultiline(2)}
  font-weight: 500;
  font-size: 16px;
  color: ${({ theme }) => theme.palette.system.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 15px;
  }
`;

export const PhoneIconWrapper = styled.div`
  padding: 6px;
  height: 30px;
  width: 30px;
`;

export const CallButton = styled(Button).attrs({
  appearance: 'transparent',
  hasPadding: false,
})<{ $isSinglePhoneNumberRevealed?: boolean }>`
  display: flex;
  position: relative;
  justify-content: flex-start;
  align-items: center;
  height: 54px;
  min-width: 220px;
  padding: 10px 25px;
  background-color: ${({ theme }) => theme.palette.system.green};
  border-radius: 14px;
  gap: 5px;
  color: ${({ theme }) => theme.palette.system.white};
  font-size: 15px;
  font-weight: 400;
  margin-right: 20px;

  ${({ $isSinglePhoneNumberRevealed }) =>
    $isSinglePhoneNumberRevealed &&
    css`
      font-size: 18px;
      font-weight: 500;
      background-color: transparent;
      border: 1px solid ${({ theme }) => theme.palette.system.green};
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: center;
    width: 50px;
    min-width: 50px;
    height: 50px;
    padding: 0;
    border-radius: 50%;
    margin-right: 0;
  }
`;

export const HeadButton = styled(Button)`
  width: 54px;
  height: 54px;
  padding: 15px;
  border-radius: 14px;

  @media (hover: hover) {
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }
`;

export const BookmarkButton = styled(HeadButton)`
  padding: 15px;
  border-radius: 14px;

  @media (hover: hover) {
    &:hover {
      & svg path {
        stroke: ${({ theme }) => theme.palette.brand.main};
      }
    }
  }
`;

export const TitleAndPriceContainer = styled.div<{ $isCentered?: boolean }>`
  display: flex;
  row-gap: 5px;
  column-gap: 20px;
  flex-wrap: nowrap;
  align-items: center;
  margin-right: 40px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    margin: 0;

    ${({ $isCentered }) =>
      $isCentered &&
      css`
        justify-content: center;
        height: 50px;
      `}
  }
`;

export const TitleSeparator = styled.div`
  width: 1px;
  flex-shrink: 0;
  align-self: stretch;
  background-color: ${({ theme }) => theme.palette.secondary.labels};
`;

export const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
`;

export const DropdownPhonesList = styled.ul`
  padding: 10px;
  width: 220px;
  list-style: none;
`;

export const DropdownPhoneItem = styled.a`
  display: flex;
  align-items: center;
  height: 40px;
  font-size: 18px;
  font-weight: 500;
  gap: 5px;
  padding: 0 10px 0 15px;
  color: ${({ theme }) => theme.palette.primary.labels};
`;

export const ArrowButton = styled(Button).attrs({ appearance: 'transparent' })<{
  $direction: 'left' | 'right';
}>`
  position: fixed;
  z-index: 10101;
  top: 50%;
  transform: translateY(-50%);
  right: 0;
  border-radius: 7px 0 0 7px;
  width: 60px;
  height: 90px;

  background-image: url('/images/arrow.svg');
  background-position: center;
  background-repeat: no-repeat;
  background-size: 12px 22px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  ${({ $direction }) =>
    $direction === 'left' &&
    css`
      right: unset;
      left: 0;
      transform: translateY(-50%) rotate(180deg);
    `};
`;

export const DesktopCarouselSkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const DesktopThumbsSkeletonContainer = styled.div`
  display: flex;
  gap: 2px;
  margin-top: 5px;
`;
